// Data-access layer for the typing channel — supabase provider (D2 = table).
//
// Schema (Postgres): typing(code, user_id, handle, expires_at) PK
// (code, user_id). expires_at is a plain epoch-ms number (bigint column),
// matching the Firestore impl — no server-authoritative timestamp needed; the
// client-side expires_at filter is what hides stale pills.
//
// All writes are best-effort (try/catch swallowed): typing is transient UI
// state. A stale pill at worst, which the client filters out via expires_at.
import { getClient } from './client.js';
import { maintainCollection } from './realtime.js';

// 4-second TTL matches the design spec (auto-stop on 4 s idle).
export const TYPING_TTL_MS = 4000;

/**
 * Announce that `userId` is typing in `code`. Refreshes expires_at on every
 * call (= now + TYPING_TTL_MS). Idempotent upsert keyed on (code, user_id).
 * Best-effort — swallowed.
 */
export async function startTyping(code, userId, handle) {
  if (!code || !userId) return;
  try {
    await getClient()
      .from('typing')
      .upsert(
        {
          code,
          user_id: userId,
          handle: handle || '',
          expires_at: Date.now() + TYPING_TTL_MS,
        },
        { onConflict: 'code,user_id' },
      );
  } catch {
    /* best-effort — stale pill ages out via expires_at */
  }
}

/**
 * Stop the typing signal. Best-effort delete; a missed delete is filtered out
 * client-side via expires_at. Swallowed.
 */
export async function stopTyping(code, userId) {
  if (!code || !userId) return;
  try {
    await getClient()
      .from('typing')
      .delete()
      .eq('code', code)
      .eq('user_id', userId);
  } catch {
    /* best-effort */
  }
}

/**
 * Subscribe to typing entries for `code`. Yields the FULL array of non-expired
 * entries (contract §4); rows with expires_at < now are dropped client-side.
 * Returns the idempotent unsubscribe fn. Each entry:
 *   { id, handle, avatarSeed, expiresAt }.
 */
export function subscribeTyping(code, cb) {
  if (!code) return () => {};
  return maintainCollection({
    table: 'typing',
    filter: { column: 'code', value: code },
    mapRow: (row) => ({
      id: row.user_id,
      handle: row.handle || '',
      avatarSeed: row.user_id,
      expiresAt: typeof row.expires_at === 'number' ? row.expires_at : 0,
    }),
    postFilter: (e) => e.expiresAt >= Date.now(),
    cb,
  });
}
